import type { Feature } from "@/shared/rbac";
import type { ProductTourId } from "@/frontend/stores/productTourStore";

export interface ProductTourStep {
  /** Matches a `data-tour` attribute on the element to point at. */
  target: string;
  title: string;
  body: string;
  /** Left out for people who can't open this feature. Steps whose target isn't on screen are skipped too. */
  feature?: Feature;
}

export interface ProductTour {
  id: ProductTourId;
  name: string;
  /** Page under /org/:orgId/app/ where the walkthrough runs. */
  path: string;
  /** Also run on pages below `path`, such as each Settings section. */
  includeSubpages?: boolean;
  feature?: Feature;
  /** The last step offers to open this walkthrough's page next. */
  next?: ProductTourId;
  steps: ProductTourStep[];
}

export const PRODUCT_TOURS: Record<ProductTourId, ProductTour> = {
  dashboard: {
    id: "dashboard",
    name: "Dashboard",
    path: "dashboard",
    feature: "dashboard",
    next: "inbox",
    steps: [
      { target: "setup-checklist", title: "Your next steps", body: "This list shows what to set up next, like connecting another channel or inviting your team." },
      { target: "nav-inbox", feature: "inbox", title: "Inbox", body: "Every WhatsApp and Instagram conversation lands here. Read and reply to customers in one place." },
      { target: "nav-jobs", feature: "flows", title: "Jobs", body: "Jobs watch the conversations you choose and act on them, like saving order details to Google Sheets." },
      { target: "nav-campaigns", feature: "campaigns", title: "Campaigns", body: "Send broadcasts and set up automatic messages, like comment-to-DM. Your campaigns live here." },
      { target: "nav-ai-agent", feature: "autopilot", title: "AI Agent", body: "Let AI reply to customers for you, using your instructions and knowledge." },
      { target: "nav-chat", feature: "chat", title: "Que", body: "Your assistant for the whole workspace. Ask “Who asked about pricing this week?” or tell it to create a task." },
      { target: "nav-settings", title: "Settings", body: "Manage your profile and notifications. Admins also connect channels, invite the team, and manage the plan." },
    ],
  },
  inbox: {
    id: "inbox",
    name: "Inbox",
    path: "inbox",
    includeSubpages: true,
    feature: "inbox",
    steps: [
      { target: "inbox-list", title: "Your conversations", body: "New messages show up here, newest first. Open one to read the chat and reply." },
      { target: "inbox-streams", title: "Escalated chats", body: "When the AI Agent hands a chat to a person, it shows up under Escalated." },
      { target: "inbox-search", title: "Find a chat fast", body: "Search by name, or use the filter to show unread or starred chats, one channel, a label, or a lead stage." },
      { target: "inbox-new-message", title: "Start a conversation", body: "Message a customer first, right from here." },
    ],
  },
  chat: {
    id: "chat",
    name: "Que",
    path: "chat",
    includeSubpages: true,
    feature: "chat",
    steps: [
      { target: "chat-tabs", title: "Ask or search", body: "Que answers questions about your conversations, leads, tasks and campaigns. Search finds exact words across every message." },
      { target: "chat-input", title: "Ask, or ask it to act", body: "Try “Who asked for a refund this month?” or “Create a follow-up task for Ravi”. Before Que messages a customer or deletes anything, it waits for your Confirm." },
      { target: "chat-filters", title: "Narrow it down", body: "Limit the AI to certain chats, dates, or people when you need a focused answer." },
      { target: "chat-new", title: "Start a new chat", body: "Your earlier chats stay in the list below, so you can pick one up again." },
    ],
  },
  alerts: {
    id: "alerts",
    name: "Alerts",
    path: "alerts",
    feature: "alerts",
    steps: [
      { target: "alerts-header", title: "Alerts", body: "Alerts watch important conversations and tell your team when something needs attention." },
      { target: "alerts-new", title: "Create an alert", body: "Pick what to watch for, like a keyword or a slow reply, and how to notify you: email, push, WhatsApp, or webhook." },
      { target: "alerts-tabs", title: "Reminders and history", body: "Reminders holds follow-ups you set on chats. Notifications shows every alert that fired." },
    ],
  },
  flows: {
    id: "flows",
    name: "Jobs",
    path: "jobs",
    feature: "flows",
    steps: [
      { target: "flows-header", title: "Jobs", body: "Jobs watch the conversations you choose and act on them, like creating a lead or saving details to Google Sheets." },
      { target: "flows-wizard", title: "Build your first job", body: "Pick a goal, choose the conversations to watch, then decide what happens." },
      { target: "flows-new", title: "Add another job", body: "Start a new job, import one from a file, or open the visual builder." },
      { target: "flows-list", title: "Your jobs", body: "See each job's status here. Open one to edit it or check what it did." },
    ],
  },
  campaigns: {
    id: "campaigns",
    name: "Campaigns",
    path: "campaigns",
    feature: "campaigns",
    steps: [
      { target: "campaigns-new", title: "Create a campaign", body: "Send a broadcast, reply to keywords or comments automatically, or build a journey of messages over time." },
      { target: "campaigns-filters", title: "Find a campaign", body: "Filter by type, sending service, or status." },
      { target: "campaigns-view", title: "List or calendar", body: "Switch to the calendar to see when scheduled messages go out." },
      { target: "campaigns-list", title: "Your campaigns", body: "Every campaign shows up here. Open one to see who it reached and how they replied." },
    ],
  },
  autopilot: {
    id: "autopilot",
    name: "AI Agent",
    path: "ai-agent",
    feature: "autopilot",
    steps: [
      { target: "autopilot-new", title: "Set up your AI Agent", body: "Choose how it replies, set its limits, then test it before it talks to customers." },
      { target: "autopilot-list", title: "Your AI Agents", body: "Turn each one on or off here. Chats it can't handle go to the Inbox under Escalated." },
      { target: "autopilot-influence", title: "See the impact", body: "The influence report shows how AI Agent replies affected your conversations." },
    ],
  },
  pulse: {
    id: "pulse",
    name: "Pulse",
    path: "pulse",
    feature: "pulse",
    steps: [
      { target: "pulse-header", title: "Pulse", body: "Pulse checks your conversations on a schedule and surfaces the signals that matter." },
      { target: "pulse-composer", title: "Describe what to watch", body: "Write it in plain words, like “customers unhappy with delivery”. Each check costs about one credit per conversation." },
      { target: "pulse-ideas", title: "Start from an idea", body: "Pick an example monitor and adjust it to fit your business." },
      { target: "pulse-monitors", title: "Your monitors", body: "Open a monitor to see what it found." },
    ],
  },
  tasks: {
    id: "tasks",
    name: "Tasks",
    path: "tasks",
    feature: "tasks",
    steps: [
      { target: "tasks-board", title: "Your task board", body: "Move tasks across lanes as work moves forward. Querygen can also create tasks from conversations." },
      { target: "tasks-create", title: "Add a task", body: "Create one yourself, assign it, and set a due date." },
      { target: "tasks-views", title: "Board, list, or reports", body: "Switch views to scan everything in a list or see how work is going." },
      { target: "tasks-filters", title: "Focus on what's yours", body: "Filter by person, status, label, priority, or due date." },
    ],
  },
  sales: {
    id: "sales",
    name: "Sales",
    path: "sales",
    includeSubpages: true,
    feature: "sales",
    steps: [
      { target: "sales-nav", title: "Sales", body: "Track leads from your conversations. Switch between your leads and other sales views here." },
      { target: "sales-board", title: "Your lead board", body: "Each lane is a stage. Move leads along as they get closer to buying." },
      { target: "sales-add", title: "Add a lead", body: "Pick a contact from your chats to start tracking them as a lead." },
      { target: "sales-filters", title: "Find a lead", body: "Search by name, number, or company, or filter by owner and label." },
    ],
  },
  settings: {
    id: "settings",
    name: "Settings",
    path: "settings",
    includeSubpages: true,
    steps: [
      { target: "settings-nav", title: "Settings", body: "Your profile and notifications. Admins also manage the team, billing, and channels like WhatsApp and Instagram." },
      { target: "settings-content", title: "Make changes here", body: "The section you pick opens here. Some sections can only be changed by admins." },
    ],
  },
};

export function getTourForPath(pathname: string, orgId: string): ProductTour | null {
  const base = `/org/${orgId}/app/`;
  if (!pathname.startsWith(base)) return null;
  const page = pathname.slice(base.length).replace(/\/$/, "");
  return Object.values(PRODUCT_TOURS).find((tour) =>
    page === tour.path || (tour.includeSubpages && page.startsWith(`${tour.path}/`))) ?? null;
}
